import { useEffect, useState } from "react";
import api from "../lib/api";
import { money, fmtDate, financialYear, currentQuarter } from "../lib/format";
import { Users, PiggyBank, Landmark, CalendarClock } from "lucide-react";

export default function PayrollPage() {
  const [period, setPeriod] = useState("year");
  const range = period === "year" ? financialYear() : currentQuarter();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/super", { params: { date_from: range.from, date_to: range.to } }).then((r) => setData(r.data));
  }, [range.from, range.to]);

  if (!data) return <div className="text-muted-foreground">Loading…</div>;

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Payroll & Super</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Wages, PAYG withholding and Super Guarantee ({Math.round(data.super_rate * 100)}%) by quarter · {range.label}
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-border p-1 bg-card">
          <button onClick={() => setPeriod("quarter")} data-testid="payroll-period-quarter"
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${period === "quarter" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Quarter</button>
          <button onClick={() => setPeriod("year")} data-testid="payroll-period-year"
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${period === "year" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Full year</button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          { id: "wages", label: "Total wages (W1)", value: data.total_wages, icon: Users, tone: "bg-primary/10 text-primary" },
          { id: "payg", label: "PAYG withheld (W2)", value: data.total_payg, icon: Landmark, tone: "bg-amber-50 text-amber-600" },
          { id: "super", label: "Super payable (SG)", value: data.total_super, icon: PiggyBank, tone: "bg-emerald-50 text-emerald-600" },
        ].map((s) => (
          <div key={s.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`payroll-kpi-${s.id}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider font-mono text-muted-foreground">{s.label}</span>
              <span className={`w-8 h-8 rounded-lg grid place-items-center ${s.tone}`}><s.icon className="w-4 h-4" /></span>
            </div>
            <div className="num text-2xl font-bold mt-3">{money(s.value)}</div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="px-4 py-3 bg-secondary/50 font-heading font-semibold text-sm flex items-center gap-2">
          <CalendarClock className="w-4 h-4" /> Super Guarantee by quarter (due 28 days after quarter end)
        </div>
        {data.quarters.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-sm">
            No wages recorded. Add an expense and toggle "Wages payment" with a super amount.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {data.quarters.map((q) => {
              const overdue = q.due < today && q.super > 0;
              return (
                <div key={q.label} className="flex items-center justify-between px-5 py-4" data-testid={`super-quarter-${q.label.replace(/[^a-zA-Z0-9]+/g, "-")}`}>
                  <div>
                    <div className="font-medium text-sm">{q.label}</div>
                    <div className="text-xs text-muted-foreground num">
                      Wages {money(q.wages)} · PAYG {money(q.payg)} · Super due {fmtDate(q.due)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="num font-semibold text-emerald-600">{money(q.super)}</div>
                    <div className={`text-xs ${overdue ? "text-rose-600" : "text-muted-foreground"}`}>{overdue ? "Overdue" : "Super payable"}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground mt-4">
        Record wages via Expenses → toggle "Wages payment", enter PAYG withheld and Super. SG rate is {Math.round(data.super_rate * 100)}% of ordinary earnings from 1 July 2025.
      </p>
    </div>
  );
}
